import { useMemo } from "react";
import { ConnectProto } from "@racoon/api";
import { getPickupLocationStatus } from "~/shared/utils";
import type { EnrichedPickupLocation } from "../../dashboard/components/map.dashboard";

export interface ScheduleFulfillmentBucket {
  locations: number;
  locationsPercent: number;
  volumeM3: number;
  volumePercent: number;
}

export interface ScheduleFulfillment {
  fact: ScheduleFulfillmentBucket;
  plan: ScheduleFulfillmentBucket;
  notExported: ScheduleFulfillmentBucket;
  totalLocations: number;
}

/** Sum of each job's assigned container volume (nominal capacity x container count). */
const jobVolumeM3 = (job: Record<string, any>): number => {
  const containerType = job?.containerGroup?.containerType ?? job?.containerType;
  const count = Number(job?.containerGroup?.numberOfContainers ?? 0);
  return Number(containerType?.volume ?? 0) * count;
};

const pct = (value: number, total: number) => (total > 0 ? Number(((value / total) * 100).toFixed(1)) : 0);

/**
 * Buckets the selected day's pickup locations into fact/plan/not-exported, mirroring
 * the dashboard bottom sheet's status logic (getPickupLocationStatus), plus the
 * exported volume (m3) per bucket derived from each location's job container groups.
 * Locations with no job in a "waiting" state (e.g. unclassified) fall into `plan`.
 */
export const useScheduleFulfillment = (
  pickupLocations: EnrichedPickupLocation[],
): ScheduleFulfillment => {
  return useMemo(() => {
    let factLocations = 0;
    let planLocations = 0;
    let notExportedLocations = 0;
    let factVolume = 0;
    let planVolume = 0;
    let notExportedVolume = 0;

    for (const loc of pickupLocations) {
      const status = getPickupLocationStatus(loc);
      const volume = (loc._jobs ?? []).reduce(
        (sum, job) => sum + jobVolumeM3(job as Record<string, any>),
        0,
      );

      switch (status) {
        case ConnectProto.JobStatus.COMPLETED:
        case ConnectProto.JobStatus.FORCE_COMPLETED:
          factLocations++;
          factVolume += volume;
          break;
        case ConnectProto.JobStatus.NOT_COMPLETED:
          notExportedLocations++;
          notExportedVolume += volume;
          break;
        default:
          planLocations++;
          planVolume += volume;
      }
    }

    const totalLocations = factLocations + planLocations + notExportedLocations;
    const totalVolume = factVolume + planVolume + notExportedVolume;

    return {
      fact: {
        locations: factLocations,
        locationsPercent: pct(factLocations, totalLocations),
        volumeM3: factVolume,
        volumePercent: pct(factVolume, totalVolume),
      },
      plan: {
        locations: planLocations,
        locationsPercent: pct(planLocations, totalLocations),
        volumeM3: planVolume,
        volumePercent: pct(planVolume, totalVolume),
      },
      notExported: {
        locations: notExportedLocations,
        locationsPercent: pct(notExportedLocations, totalLocations),
        volumeM3: notExportedVolume,
        volumePercent: pct(notExportedVolume, totalVolume),
      },
      totalLocations,
    };
  }, [pickupLocations]);
};
